/**
 * InviteAcceptPage — validates and accepts workspace invitations.
 *
 * Route: /invite/:token (outside ProtectedRoute — validate is public)
 *
 * Flow:
 *   1. Validate token via GET /api/auth/invitations/:token
 *   2. Show workspace name, role, inviter
 *   3. If not authenticated → redirect to /login with returnTo
 *   4. Accept button → POST /api/auth/invitations/:token/accept → redirect to /
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi, type InvitationValidation } from '../api/auth';

type PageState = 'loading' | 'valid' | 'accepting' | 'error' | 'expired';

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [state, setState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<InvitationValidation | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Validate the token on mount
  useEffect(() => {
    if (!token) {
      setState('error');
      setError('No invitation token provided.');
      return;
    }

    const validate = async () => {
      try {
        const data = await authApi.validateInvitation(token);
        setInvitation(data);
        setState('valid');
      } catch (err) {
        if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 410) {
          setState('expired');
        } else {
          setState('error');
          setError('This invitation is invalid or has already been used.');
        }
      }
    };

    void validate();
  }, [token]);

  // If not authenticated after loading, redirect to login
  useEffect(() => {
    if (!authLoading && !isAuthenticated && state === 'valid') {
      navigate('/login', { state: { returnTo: `/invite/${token}` }, replace: true });
    }
  }, [authLoading, isAuthenticated, state, navigate, token]);

  const handleAccept = async () => {
    if (!token) return;
    setState('accepting');
    try {
      await authApi.acceptInvitation(token);
      navigate('/', { replace: true });
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Failed to accept invitation.');
    }
  };

  if (state === 'loading' || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-coral-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-neutral-400">Validating invitation…</p>
        </div>
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-warm-md border border-neutral-200 p-8 text-center">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Invitation expired</h2>
          <p className="text-sm text-neutral-500 mb-6">
            This invitation has expired. Please ask the workspace admin to send a new one.
          </p>
          <a href="/" className="inline-flex items-center px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium rounded-lg transition">
            Go to dashboard
          </a>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-warm-md border border-neutral-200 p-8 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Invalid invitation</h2>
          <p className="text-sm text-neutral-500 mb-6">{error}</p>
          <a href="/" className="inline-flex items-center px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium rounded-lg transition">
            Go to dashboard
          </a>
        </div>
      </div>
    );
  }

  // Valid invitation — show accept UI
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-coral-500 rounded-xl mb-4">
            <span className="text-white text-xl font-bold">M</span>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900">You're invited</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-warm-md border border-neutral-200 p-8">
          <div className="text-center mb-6">
            {invitation?.invitedBy?.name && (
              <p className="text-sm text-neutral-500 mb-1">
                <span className="font-medium text-neutral-700">{invitation.invitedBy.name}</span> invited you
              </p>
            )}
            <p className="text-sm text-neutral-500">
              to join as <span className="font-medium text-neutral-700 capitalize">{invitation?.role}</span>
            </p>
          </div>

          <button
            onClick={() => { void handleAccept(); }}
            disabled={state === 'accepting'}
            className="w-full py-2.5 px-4 bg-coral-500 hover:bg-coral-600 disabled:bg-neutral-300 text-white text-sm font-medium rounded-lg transition focus:outline-none focus:ring-1 focus:ring-coral-500"
          >
            {state === 'accepting' ? 'Joining…' : 'Accept invitation'}
          </button>
        </div>
      </div>
    </div>
  );
}
